import { chain } from '@leprecoins/effector';
import { createForm } from '@leprecoins/effector-forms';
import { combine, sample } from 'effector';
import { createBlockUserModel } from 'features/block-user';
import { createDeleteUserModel } from 'features/delete-user';
import { createUnblockUserModel } from 'features/unblock-user';
import {
	USER_NAME_MAX_LENGTH,
	USER_NAME_MIN_LENGTH,
	USER_NOTE_MAX_LENGTH,
	USER_PASSWORD_MIN_LENGTH,
} from 'entities/user';
import { viewerModel } from 'entities/viewer';
import { createEditUserMutation, createGetUserQuery, USER_ROLES, UserRole } from 'shared/api';
import {
	emptyOrMinLengthRule,
	maxLengthRule,
	minLengthRule,
	notEmptyRule,
	notify,
} from 'shared/lib';
import { authorizedUserRoute, router } from 'shared/routing';

const getUserQuery = createGetUserQuery();

const editUserMutation = createEditUserMutation();

const blockUserModel = createBlockUserModel();

const unblockUserModel = createUnblockUserModel();

const deleteUserModel = createDeleteUserModel();

const form = createForm({
	fields: {
		name: {
			init: '',
			rules: [
				notEmptyRule,
				minLengthRule(USER_NAME_MIN_LENGTH),
				maxLengthRule(USER_NAME_MAX_LENGTH),
			],
		},
		password: {
			init: '',
			rules: [emptyOrMinLengthRule(USER_PASSWORD_MIN_LENGTH)],
		},
		role: {
			init: USER_ROLES.admin as UserRole,
		},
		note: {
			init: '',
			rules: [maxLengthRule(USER_NOTE_MAX_LENGTH)],
		},
		hasMultiLogin: {
			init: false,
		},
	},
	validateOn: ['submit'],
	resetErrorsOnChange: true,
});

const $user = getUserQuery.$data;

const $cannotEdit = combine(
	{
		profile: viewerModel.$profile,
		user: $user,
	},
	({ profile, user }) => {
		if (!user) return false;

		return (
			profile?.id === user.id ||
			(profile?.role !== USER_ROLES.god &&
				profile?.id !== user.id &&
				user.role === USER_ROLES.god) ||
			(profile?.role === USER_ROLES.admin && user.role === USER_ROLES.admin)
		);
	},
);

sample({
	clock: authorizedUserRoute.opened,
	fn: (route) => route.params.id,
	target: getUserQuery.start,
});

sample({
	clock: getUserQuery.finished.success,
	fn: ({ result }) => ({
		name: result.name,
		password: '',
		role: result.role,
		note: result.note ?? '',
		hasMultiLogin: result.hasMultiLogin,
	}),
	target: [form.setInitialValues, form.setValues],
});

sample({
	clock: form.submitted,
	source: { values: form.$values, user: $user },
	fn: ({ values, user }) => ({
		id: user!.id,
		name: values.name,
		password: values.password || undefined,
		role: values.role,
		note: values.note,
		hasMultiLogin: values.hasMultiLogin,
	}),
	target: editUserMutation.start,
});

sample({
	clock: [
		editUserMutation.finished.success,
		blockUserModel.mutation.finished.success,
		unblockUserModel.mutation.finished.success,
	],
	source: $user,
	filter: Boolean,
	fn: ({ id }) => id,
	target: getUserQuery.start,
});

chain(deleteUserModel.mutation.finished.success, router.back);

sample({
	clock: authorizedUserRoute.closed,
	target: [form.reset, getUserQuery.reset, editUserMutation.reset],
});

notify(editUserMutation.finished.success, 'edit-user.success');

export const userPageModel = {
	getUserQuery,
	editUserMutation,
	blockUserModel,
	unblockUserModel,
	deleteUserModel,
	form,
	$user,
	$cannotEdit,
};
